import { memo } from 'react';
import { useDrag } from 'react-dnd';
import clsx from 'clsx';
import { ExportOutlined } from '@ant-design/icons';
import TruncatedText from '../../../../../shared/components/TruncatedText';
import type { WorkOrderDragItem, WorkOrderListItem } from '../../../../../types/InfrastructureManagerTypes';
import { WORK_ORDER_DRAG_TYPE } from '../../constants';
import styles from './WorkOrderRow.module.scss';

const LINK_OUT_ICON = <ExportOutlined aria-hidden />;

interface WorkOrderRowProps {
  order: WorkOrderListItem;
  isSelected: boolean;
  canDrag: boolean;
  onSelect: (id: number) => void;
  onExpand: (id: number) => void;
}

const WorkOrderRow = ({ order, isSelected, canDrag, onSelect, onExpand }: WorkOrderRowProps) => {
  const hasScheduleDates = order.scheduleDates.length > 0;

  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: WORK_ORDER_DRAG_TYPE,
      item: { id: order.id, from: order.status, hasScheduleDates } satisfies WorkOrderDragItem,
      canDrag,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [order.id, order.status, hasScheduleDates, canDrag],
  );

  const handleSelect = () => onSelect(order.id);
  const handleExpand = () => onExpand(order.id);

  const rowClassName = clsx(styles.workOrderRow, {
    [styles.workOrderRowSelected]: isSelected,
    [styles.workOrderRowDragging]: isDragging,
  });

  return (
    <li ref={dragRef} className={rowClassName}>
      <button
        type="button"
        className={styles.workOrderRowExpand}
        aria-label={`Open ${order.title} details`}
        onClick={handleExpand}
      >
        {LINK_OUT_ICON}
      </button>
      <button type="button" className={styles.workOrderRowTitle} aria-pressed={isSelected} onClick={handleSelect}>
        <TruncatedText text={order.title} />
      </button>
    </li>
  );
};

export default memo(WorkOrderRow);
